import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { menuItems } from "./menuItems";
import { AgentContext } from "../../context/AgentContext";
import { useTheme } from "../../context/ThemeContext";
import { useLanguage } from "../../context/LanguageContext";
import { createAgent, deleteAgent, updateAgentById } from "../../services/agentService";
import {
  Bot,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Plus,
  Trash2,
  X,
  MessageSquare,
  Pencil,
  Check,
  Sparkles,
  Sun,
  Moon,
  Pin,
  PinOff,
  ExternalLink,
  PanelLeft,
} from "lucide-react";

const ADMIN_EMAILS = ["alfa271200@gmail.com"];

function getStoredPinnedAgents() {
  try {
    const storedUser = localStorage.getItem("user");
    const parsed = storedUser ? JSON.parse(storedUser) : null;
    const email = (parsed?.email || "").toLowerCase();
    const id = (parsed?.userId || parsed?.id || "").toLowerCase();
    const name = (parsed?.name || "").toLowerCase();

    // Check all possible key formats in order of specificity
    const candidateKeys = [
      email ? `tacia_pinned_agents_${email}` : null,
      id ? `tacia_pinned_agents_${id}` : null,
      name ? `tacia_pinned_agents_${name}` : null,
      "tacia_pinned_agents",
      "tacia_pinned_agents_user",
      "tacia_pinned_agents_default",
    ].filter(Boolean);

    for (const key of candidateKeys) {
      const saved = localStorage.getItem(key);
      if (saved) {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr) && arr.length > 0) return arr;
      }
    }

    // Fallback: scan any localStorage key starting with tacia_pinned_agents
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("tacia_pinned_agents")) {
        const saved = localStorage.getItem(key);
        if (saved) {
          const arr = JSON.parse(saved);
          if (Array.isArray(arr) && arr.length > 0) return arr;
        }
      }
    }
  } catch (err) {
    console.error("Gagal membaca pinned agents:", err);
  }
  return [];
}

export default function Sidebar({ mobile = false, onClose, onNavigate }) {
  const { agents, refreshAgents } = useContext(AgentContext);
  const { theme, isLight, toggleTheme } = useTheme();
  const { language, isIndonesian, isEnglish, toggleLanguage, t } = useLanguage();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem("tacia_sidebar_collapsed") === "true";
    } catch {
      return false;
    }
  });

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("tacia_sidebar_collapsed", String(next));
      } catch {}
      return next;
    });
  };

  const [user, setUser] = useState({ name: "Guest", company: "Company" });
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [pinnedIds, setPinnedIds] = useState(() => getStoredPinnedAgents());
  const [pinNotice, setPinNotice] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const collapsed = mobile ? false : isCollapsed;

  useEffect(() => {
    refreshAgents();

    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser({
          name: parsed.name || "User",
          company: parsed.company || "Enterprise",
        });

        const email = (parsed.email || "").toLowerCase();
        setIsAdmin(ADMIN_EMAILS.includes(email));

        const loadedPinned = getStoredPinnedAgents();
        if (loadedPinned && loadedPinned.length > 0) {
          setPinnedIds(loadedPinned);
        }
      } catch (e) {
        console.error("Gagal parsing user data");
        setIsAdmin(false);
      }
    } else {
      setIsAdmin(false);
    }
  }, [refreshAgents]);

  const savePinnedIds = (nextIds) => {
    setPinnedIds(nextIds);
    try {
      const storedUser = localStorage.getItem("user");
      const parsed = storedUser ? JSON.parse(storedUser) : null;
      const email = (parsed?.email || "").toLowerCase();
      const id = (parsed?.userId || parsed?.id || "").toLowerCase();
      const name = (parsed?.name || "").toLowerCase();
      const userKey = email || id || name || "user";

      // Save to both specific user key and global key for cross-session resilience
      localStorage.setItem(`tacia_pinned_agents_${userKey}`, JSON.stringify(nextIds));
      localStorage.setItem("tacia_pinned_agents", JSON.stringify(nextIds));
      if (email) localStorage.setItem(`tacia_pinned_agents_${email}`, JSON.stringify(nextIds));
      if (id) localStorage.setItem(`tacia_pinned_agents_${id}`, JSON.stringify(nextIds));
    } catch (e) {
      console.error("Gagal simpan pin:", e);
    }
  };

  const handleTogglePin = (e, agentId) => {
    e.preventDefault();
    e.stopPropagation();

    const strId = String(agentId);
    const isAlreadyPinned = pinnedIds.some((id) => String(id) === strId);

    if (isAlreadyPinned) {
      const next = pinnedIds.filter((id) => String(id) !== strId);
      savePinnedIds(next);
      setPinNotice("");
    } else {
      if (pinnedIds.length >= 3) {
        setPinNotice("Maksimal 3 riwayat konsultasi yang dapat disematkan.");
        setTimeout(() => {
          setPinNotice("");
        }, 3000);
        return;
      }
      const next = [agentId, ...pinnedIds];
      savePinnedIds(next);
      setPinNotice("");
    }
  };

  const handleLogout = () => {
    if (!window.confirm(isIndonesian ? "Yakin mau logout?" : "Are you sure you want to sign out?")) {
      return;
    }

    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
    onClose?.();
  };

  const handleCreate = async () => {
    try {
      const newAgent = {
        agentName: isIndonesian ? "Konsultasi Baru" : "New Consultation",
        agentPersonality: "Professional",
        agentPrompt: isIndonesian
          ? "Kamu asisten AI perpajakan..."
          : "You are TACIA, an expert AI tax consultant...",
        businessDescription: "",
      };
      await createAgent(newAgent);
      refreshAgents();
      onNavigate?.();
    } catch (err) {
      alert(isIndonesian ? "Gagal membuat sesi konsultasi baru" : "Failed to create new consultation session");
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm(t("sidebar.deleteConfirm"))) {
      return;
    }

    try {
      await deleteAgent(agentId);
      if (pinnedIds.includes(agentId)) {
        savePinnedIds(pinnedIds.filter((id) => id !== agentId));
      }
      refreshAgents();
    } catch (err) {
      console.error(err);
    }
  };

  const handleStartRename = (e, agent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingId(agent.id);
    setEditName(agent.agentName || "");
  };

  const handleSaveRename = async (agentId) => {
    const trimmed = editName.trim();
    if (!trimmed) {
      setEditingId(null);
      return;
    }
    try {
      await updateAgentById(agentId, { agentName: trimmed });
      refreshAgents();
    } catch (err) {
      console.error("Gagal rename:", err);
    }
    setEditingId(null);
  };

  const handleRenameKeyDown = (e, agentId) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveRename(agentId);
    } else if (e.key === "Escape") {
      setEditingId(null);
    }
  };

  const validPinnedIds = pinnedIds.filter((id) => (agents || []).some((a) => String(a.id) === String(id)));
  const pinnedAgents = (agents || [])
    .filter((agent) => validPinnedIds.some((id) => String(id) === String(agent.id)))
    .sort((a, b) => {
      const idxA = validPinnedIds.findIndex((id) => String(id) === String(a.id));
      const idxB = validPinnedIds.findIndex((id) => String(id) === String(b.id));
      return idxA - idxB;
    });
  const unpinnedAgents = (agents || []).filter((agent) => !validPinnedIds.some((id) => String(id) === String(agent.id)));

  const renderAgentItem = (agent, isPinned) => {
    const originalIdx = (agents || []).findIndex((a) => a.id === agent.id);
    const prefix = isIndonesian ? "Konsultasi" : "Consultation";
    const displayName =
      agent.agentName === "New Agent" || agent.agentName === "Chat Baru" || agent.agentName === "Konsultasi Baru" || agent.agentName === "New Consultation"
        ? `${prefix} #${(agents || []).length - (originalIdx !== -1 ? originalIdx : 0)}`
        : agent.agentName;

    return (
      <li key={agent.id} className="group relative">
        <NavLink
          to={`/agents/${agent.id}`}
          onClick={editingId === agent.id ? (e) => e.preventDefault() : onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-xs transition-all duration-200 ${
              isActive
                ? isLight
                  ? "bg-white border border-[#3273D9]/40 text-[#1B2422] font-semibold shadow-sm"
                  : "bg-gradient-to-r from-[#17303E] to-[#12232E] border-l-[3px] border-l-[#38BDF8] border-y border-r border-[#38BDF8]/40 text-[#F8FAFC] font-semibold shadow-md shadow-[#38BDF8]/10"
                : isLight
                ? "text-[#5C6B66] hover:bg-black/5 hover:text-[#1B2422]"
                : "text-[#94A3B8] hover:bg-white/[0.06] hover:text-[#F8FAFC]"
            }`
          }
          title={isPinned ? `[${t("sidebar.pinned")}] ${displayName}` : displayName}
        >
          <div className="relative shrink-0 flex items-center justify-center">
            <MessageSquare
              size={15}
              className={isLight ? "text-[#3273D9]" : "text-[#38BDF8]"}
            />
            {isPinned && collapsed && (
              <span className={`absolute -top-1 -right-1 flex h-2 w-2 rounded-full ${isLight ? "bg-[#3273D9]" : "bg-[#38BDF8]"}`} title={t("sidebar.pinned")} />
            )}
          </div>

          {!collapsed && (
            <>
              {editingId === agent.id ? (
                <div
                  className="flex flex-1 items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => handleRenameKeyDown(e, agent.id)}
                    onBlur={() => handleSaveRename(agent.id)}
                    autoFocus
                    className={`w-full rounded border px-1.5 py-0.5 text-xs outline-none ${
                      isLight
                        ? "border-[#3273D9] bg-white text-[#1B2422]"
                        : "border-[#3273D9] bg-[#111819] text-[#F5F5F0]"
                    }`}
                  />
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSaveRename(agent.id);
                    }}
                    className="rounded p-1 text-[#3F6B52] hover:bg-black/5"
                  >
                    <Check size={13} />
                  </button>
                </div>
              ) : (
                <>
                  <span className="truncate flex-1">
                    {displayName}
                  </span>

                  {/* Pinned indicator when NOT hovering */}
                  {isPinned && (
                    <span
                      className="shrink-0 text-[#3273D9] group-hover:hidden"
                      title={t("sidebar.pinnedBadge")}
                    >
                      <Pin size={12} className="rotate-45" fill="currentColor" />
                    </span>
                  )}

                  {/* Hover action buttons */}
                  <div className="hidden items-center gap-0.5 group-hover:flex shrink-0">
                    <button
                      type="button"
                      onClick={(e) => handleTogglePin(e, agent.id)}
                      className={`rounded p-1 transition ${
                        isPinned
                          ? "text-[#3273D9] hover:bg-black/5 dark:hover:bg-white/5"
                          : isLight
                          ? "text-[#7A8A85] hover:text-[#3273D9] hover:bg-black/5"
                          : "text-[#707E7A] hover:text-[#3273D9] hover:bg-white/5"
                      }`}
                      title={isPinned ? t("sidebar.unpinTooltip") : t("sidebar.pinTooltip")}
                    >
                      {isPinned ? (
                        <PinOff size={13} />
                      ) : (
                        <Pin size={13} className="rotate-45" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleStartRename(e, agent)}
                      className={`rounded p-1 transition ${
                        isLight ? "text-[#7A8A85] hover:text-[#3273D9] hover:bg-black/5" : "text-[#707E7A] hover:text-[#3273D9] hover:bg-white/5"
                      }`}
                      title={t("sidebar.renameTooltip")}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteAgent(agent.id);
                      }}
                      className={`rounded p-1 transition ${
                        isLight ? "text-[#7A8A85] hover:text-red-600 hover:bg-black/5" : "text-[#707E7A] hover:text-red-400 hover:bg-white/5"
                      }`}
                      title={t("sidebar.deleteTooltip")}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </NavLink>
      </li>
    );
  };

  return (
    <aside
      className={`relative flex flex-col h-full max-h-screen overflow-hidden border-r transition-all duration-300 ease-in-out ${
        isLight
          ? "border-[#DEE4DC] bg-[#F9FAF8] text-[#5C6B66]"
          : "border-[#243538] bg-[#0E1517] text-[#CBD5E1]"
      } ${
        mobile
          ? "fixed inset-y-0 left-0 z-50 w-[292px] shadow-2xl"
          : collapsed
          ? "w-20"
          : "w-[292px]"
      }`}
    >
      {/* BRAND HEADER */}
      <div className={`flex h-20 shrink-0 ${
        collapsed ? "flex-col items-center justify-center gap-1 px-2" : "items-center justify-between gap-3 px-4"
      } border-b transition-colors ${
        isLight ? "border-[#DEE4DC]" : "border-[#243538]"
      }`}>
        {collapsed ? (
          /* COLLAPSED HEADER: Centered Toggle Sidebar Button */
          <div className="flex w-full items-center justify-center">
            <button
              type="button"
              onClick={handleToggleCollapse}
              className={`flex h-10 w-10 items-center justify-center rounded-xl border shadow-sm transition-all duration-200 ${
                isLight
                  ? "border-[#DEE4DC] bg-white text-[#5C6B66] hover:bg-black/5 hover:text-[#1B2422] hover:border-[#CBD5E1]"
                  : "border-[#243538] bg-[#101719] text-[#94A3B8] hover:bg-white/10 hover:text-white hover:border-[#38BDF8]/40"
              }`}
              title={t("sidebar.expandSidebar")}
              aria-label={t("sidebar.expandSidebar")}
            >
              <PanelLeft size={18} />
            </button>
          </div>
        ) : (
          /* EXPANDED HEADER */
          <>
            <div className="flex shrink-0 min-w-0 items-center gap-2">
              {/* Executive Logo Mark */}
              <div className="relative flex shrink-0 items-center justify-center">
                <img 
                  src={isLight ? "/tacia-icon.png" : "/tacia-icon-dark.png"} 
                  alt="Tacia Icon" 
                  className="h-9 w-9 object-contain" 
                />
                {/* Precision status indicator */}
                <span className="absolute -bottom-0.5 -right-0.5 flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#34D399] opacity-75"></span>
                  <span className={`relative inline-flex rounded-full h-2.5 w-2.5 bg-[#34D399] ring-2 ${
                    isLight ? "ring-white" : "ring-[#0E1517]"
                  }`}></span>
                </span>
              </div>

              <div className="shrink-0 flex flex-col justify-center">
                <img 
                  src={isLight ? "/tacia-text.png" : "/tacia-text-dark.png"} 
                  alt="Tacia" 
                  className="h-4 w-auto object-contain object-left block" 
                />
                <a
                  href="https://enforcea.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1.5 inline-flex items-center gap-1 group transition-all pl-0.5"
                  style={{ textDecoration: 'none' }}
                  title={t("sidebar.poweredBy")}
                >
                  <span className={`text-[10px] font-normal leading-none tracking-[0.01em] transition-colors ${
                    isLight ? "text-[#7A8A85] group-hover:text-text-pri" : "text-[#94A3B8] group-hover:text-white"
                  }`}>
                    with
                  </span>
                  <span className={`text-[10px] font-semibold leading-none inline-flex items-center gap-0.5 transition-colors ${
                    isLight ? "text-[#C41E24] group-hover:text-[#E02029]" : "text-[#F87171] group-hover:text-[#FCA5A5]"
                  }`}>
                    EnforceA
                    <ExternalLink size={8.5} className="opacity-70 group-hover:opacity-100 transition-opacity" />
                  </span>
                </a>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1.5">
              <div className={`flex h-8 items-center rounded-lg border px-1 shadow-sm transition-all ${
                isLight
                  ? "border-[#DEE4DC] bg-white text-[#5C6B66]"
                  : "border-[#243538] bg-[#101719] text-[#94A3B8]"
              }`}>
                {/* Language Switcher Button (ID | EN) */}
                <button
                  type="button"
                  onClick={toggleLanguage}
                  className={`flex h-6 items-center gap-1 rounded px-1.5 text-[10px] font-bold leading-none tracking-wide transition-all ${
                    isLight ? "hover:bg-black/5" : "hover:bg-white/5"
                  }`}
                  title={t("sidebar.langSwitchTooltip")}
                >
                  <span className={isIndonesian ? (isLight ? "text-[#3273D9] font-extrabold" : "text-[#38BDF8] font-extrabold") : isLight ? "text-[#94A3B8]" : "text-[#64748B]"}>ID</span>
                  <span className={`text-[9px] font-normal ${isLight ? "text-[#CBD5E1]" : "text-[#334155]"}`}>|</span>
                  <span className={isEnglish ? (isLight ? "text-[#3273D9] font-extrabold" : "text-[#38BDF8] font-extrabold") : isLight ? "text-[#94A3B8]" : "text-[#64748B]"}>EN</span>
                </button>

                {/* Vertical Divider */}
                <div className={`h-3.5 w-[1px] mx-0.5 ${isLight ? "bg-[#DEE4DC]" : "bg-[#243538]"}`} />

                {/* Theme Switcher Button */}
                <button
                  type="button"
                  onClick={toggleTheme}
                  className={`flex h-6 w-6 items-center justify-center rounded transition-all ${
                    isLight
                      ? "text-[#5C6B66] hover:bg-black/5 hover:text-[#3273D9]"
                      : "text-[#94A3B8] hover:bg-white/5 hover:text-[#38BDF8]"
                  }`}
                  title={isLight ? t("sidebar.themeToDark") : t("sidebar.themeToLight")}
                >
                  {isLight ? <Moon size={13} /> : <Sun size={13} />}
                </button>
              </div>

              {/* TOGGLE SIDEBAR BUTTON (COLLAPSE) */}
              {!mobile && (
                <button
                  type="button"
                  onClick={handleToggleCollapse}
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border shadow-sm transition-all duration-200 ${
                    isLight
                      ? "border-[#DEE4DC] bg-white text-[#5C6B66] hover:bg-black/5 hover:text-[#1B2422] hover:border-[#CBD5E1]"
                      : "border-[#243538] bg-[#101719] text-[#94A3B8] hover:bg-white/10 hover:text-white hover:border-[#38BDF8]/40"
                  }`}
                  title={t("sidebar.collapseSidebar")}
                  aria-label={t("sidebar.collapseSidebar")}
                >
                  <PanelLeft size={15} />
                </button>
              )}

              {mobile && (
                <button
                  type="button"
                  onClick={onClose}
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border shadow-sm transition-all ${
                    isLight
                      ? "border-[#DEE4DC] bg-white text-[#5C6B66] hover:bg-black/5"
                      : "border-[#243538] bg-[#101719] text-[#A8B8B2] hover:bg-white/5"
                  }`}
                  aria-label="Close navigation"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* SIDEBAR CONTENT */}
      <div className="sidebar-scroll flex-1 min-h-0 space-y-7 overflow-y-auto overscroll-contain px-3 py-6">
        <div>
          {!collapsed && (
            <p className={`mb-2.5 px-3 text-[10px] font-bold uppercase tracking-[0.18em] ${
              isLight ? "text-[#5C6B66]" : "text-[#94A3B8]"
            }`}>
              {t("sidebar.platform")}
            </p>
          )}
          <ul className="space-y-1">
            {menuItems
              .filter((item) => !item.adminOnly || isAdmin)
              .map((item, index) => (
                <li key={index}>
                  <NavLink
                    to={item.path}
                    end
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-xs font-semibold tracking-wide transition-all duration-200 ${
                        isActive
                          ? isLight
                            ? "bg-[#3273D9] text-white shadow-md shadow-[#3273D9]/20"
                            : "bg-gradient-to-r from-[#0284C7] to-[#0369A1] text-white shadow-md shadow-[#38BDF8]/25"
                          : isLight
                          ? "text-[#5C6B66] hover:bg-black/5 hover:text-[#1B2422]"
                          : "text-[#CBD5E1] hover:bg-white/[0.08] hover:text-[#F8FAFC]"
                      }`
                    }
                  >
                    <span className="shrink-0">{item.icon}</span>
                    {!collapsed && <span>{item.labelKey ? t(item.labelKey, item.label) : item.label}</span>}
                  </NavLink>
                </li>
              ))}
          </ul>
        </div>

        {/* RIWAYAT CHAT / CONSULTATION ARCHIVE */}
        <div>
          {!collapsed && (
            <div className="mb-2.5 flex items-center justify-between px-3">
              <p className={`text-[10px] font-bold uppercase tracking-[0.18em] ${
                isLight ? "text-[#5C6B66]" : "text-[#94A3B8]"
              }`}>
                {t("sidebar.consultationHistory")}
              </p>
              <button
                type="button"
                onClick={handleCreate}
                className={`rounded-md p-1.5 transition ${
                  isLight ? "text-[#3273D9] hover:bg-black/5" : "text-[#38BDF8] hover:bg-white/10"
                }`}
                title={t("sidebar.newConsultation")}
              >
                <Plus size={15} />
              </button>
            </div>
          )}
          <ul className="space-y-1">
            {pinNotice && !collapsed && (
              <li className="mb-1.5">
                <div className={`flex items-center justify-between rounded-lg border px-2.5 py-1.5 text-[11px] font-medium transition-all ${
                  isLight
                    ? "bg-amber-50 border-amber-200 text-amber-800"
                    : "bg-amber-950/50 border-amber-500/50 text-amber-200"
                }`}>
                  <span className="truncate">{pinNotice}</span>
                  <button
                    type="button"
                    onClick={() => setPinNotice("")}
                    className="ml-1 p-0.5 opacity-70 hover:opacity-100"
                  >
                    <X size={12} />
                  </button>
                </div>
              </li>
            )}

            {agents && agents.length > 0 ? (
              <>
                {pinnedAgents.length > 0 && !collapsed && (
                  <li className={`px-3 pt-0.5 pb-1 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.16em] ${
                    isLight ? "text-[#3273D9]" : "text-[#38BDF8]"
                  }`}>
                    <span className="flex items-center gap-1.5">
                      <Pin size={11} className="rotate-45" fill="currentColor" />
                      {t("sidebar.pinned")} ({pinnedAgents.length}/3)
                    </span>
                  </li>
                )}

                {pinnedAgents.map((agent) => renderAgentItem(agent, true))}

                {pinnedAgents.length > 0 && unpinnedAgents.length > 0 && (
                  <li className="py-1">
                    <div className={`border-t ${isLight ? "border-[#DEE4DC]" : "border-[#243538]"}`} />
                  </li>
                )}

                {pinnedAgents.length > 0 && unpinnedAgents.length > 0 && !collapsed && (
                  <li className={`px-3 pt-0.5 pb-1 text-[10px] font-bold uppercase tracking-[0.16em] ${
                    isLight ? "text-[#7A8A85]" : "text-[#94A3B8]"
                  }`}>
                    {t("sidebar.others")}
                  </li>
                )}

                {unpinnedAgents.map((agent) => renderAgentItem(agent, false))}
              </>
            ) : (
              !collapsed && (
                <li className={`px-3 py-4 text-center text-xs ${
                  isLight ? "text-[#7A8A85]" : "text-[#94A3B8]"
                }`}>
                  {t("sidebar.noSessions")}
                </li>
              )
            )}
          </ul>
        </div>
      </div>

      {/* USER FOOTER */}
      <div className={`border-t p-4 transition-colors ${
        isLight ? "border-[#DEE4DC]" : "border-[#243538]"
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-serif text-xs font-bold border ${
              isLight ? "bg-white border-[#DEE4DC] text-[#3273D9]" : "bg-[#141E21] border-[#243538] text-[#38BDF8]"
            }`}>
              {user.name ? user.name.charAt(0).toUpperCase() : "U"}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className={`truncate text-xs font-semibold ${
                  isLight ? "text-[#1B2422]" : "text-[#F8FAFC]"
                }`}>
                  {user.name}
                </p>
                <p className={`truncate text-[10px] ${
                  isLight ? "text-[#7A8A85]" : "text-[#94A3B8]"
                }`}>
                  {user.company || "Enterprise Account"}
                </p>
              </div>
            )}
          </div>

          {!collapsed && (
            <button
              type="button"
              onClick={handleLogout}
              className={`rounded-lg p-2 transition ${
                isLight ? "text-[#7A8A85] hover:bg-black/5 hover:text-red-600" : "text-[#94A3B8] hover:bg-white/10 hover:text-red-400"
              }`}
              title={t("sidebar.logout")}
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
